import offlineStorageService, { OfflineObservation } from './OfflineStorageService';
import observationApiService, { PhotoItem, MediaItem } from '../apiService/ObservationApiService';

type SyncListener = (result: { success: number; failed: number }) => void;

// Name of the cross-tab Web Lock used to ensure only one tab syncs at a time
const SYNC_LOCK_NAME = 'fauna-offline-sync-lock';

class OfflineSyncService {
  private isCurrentlySyncing = false;
  private maxRetries = 3;
  private retryDelayMs = 5000; // 5 seconds
  private listeners: SyncListener[] = [];

  // ---- Cross-tab safe entry point ----
  // Uses the Web Locks API so that only one tab can run the sync loop at a time.
  // Falls back to the in-memory guard for environments without Web Locks.
  async syncPendingObservations(): Promise<{ success: number; failed: number }> {
    // Fast path: if this tab is already syncing, skip
    if (this.isCurrentlySyncing) {
      return { success: 0, failed: 0 };
    }

    if (navigator.locks) {
      // Try to acquire the lock without waiting (ifAvailable).
      // If another tab holds it, we skip — that tab is already syncing.
      const result = await navigator.locks.request(
        SYNC_LOCK_NAME,
        { ifAvailable: true },
        async (lock) => {
          if (!lock) {
            // Another tab holds the lock — skip
            console.log('OfflineSyncService: Another tab is already syncing, skipping.');
            return { success: 0, failed: 0 };
          }
          return this.executeSyncLoop();
        }
      );
      return result;
    } else {
      // Fallback for browsers without Web Locks API
      console.warn('OfflineSyncService: Web Locks API not available, using in-memory guard only.');
      return this.executeSyncLoop();
    }
  }

  // The actual sync loop — must only be called while the cross-tab lock is held (or in fallback mode).
  private async executeSyncLoop(): Promise<{ success: number; failed: number }> {
    if (this.isCurrentlySyncing) {
      return { success: 0, failed: 0 };
    }

    this.isCurrentlySyncing = true;
    let successCount = 0;
    let failedCount = 0;

    try {
      // Atomically claim pending observations so no other tab can pick them up
      const claimedObservations = await offlineStorageService.claimPendingObservations();

      if (claimedObservations.length === 0) {
        console.log('OfflineSyncService: No pending observations to sync.');
        return { success: 0, failed: 0 };
      }

      console.log(`OfflineSyncService: Claimed ${claimedObservations.length} observations for sync.`);

      for (const observation of claimedObservations) {
        try {
          const success = await this.syncSingleObservation(observation);
          if (success) {
            await offlineStorageService.removeOfflineObservation(observation.id);
            successCount++;
            console.log(`Successfully synced observation ${observation.id}`);
          } else {
            // Update retry count
            const newRetryCount = observation.retryCount + 1;
            if (newRetryCount >= this.maxRetries) {
              console.error(`Max retries reached for observation ${observation.id}, removing from queue`);
              await offlineStorageService.removeOfflineObservation(observation.id);
              failedCount++;
            } else {
              await offlineStorageService.updateRetryCount(observation.id, newRetryCount);
              failedCount++;
              console.log(`Retry ${newRetryCount}/${this.maxRetries} for observation ${observation.id}`);
            }
          }
        } catch (error) {
          console.error(`Error syncing observation ${observation.id}:`, error);
          // Reset syncStatus so the record is not stuck as 'syncing' forever
          try { await offlineStorageService.resetSyncStatus(observation.id); } catch (_) { /* best effort */ }
          failedCount++;
        }
      }

      // If there are still failed items, schedule a retry
      if (failedCount > 0) {
        this.scheduleRetry();
      }

      // Notify listeners after sync completes
      this.listeners.forEach(listener => {
        try { listener({ success: successCount, failed: failedCount }); } catch (e) { /* ignore */ }
      });

    } catch (error) {
      console.error('Error during sync process:', error);
    } finally {
      this.isCurrentlySyncing = false;
    }

    return { success: successCount, failed: failedCount };
  }

  // Sync a single observation
  private async syncSingleObservation(observation: OfflineObservation): Promise<boolean> {
    try {
      const { type, observationId, observationData, photos, media, mediaTypes, mediaThumbnails, photosToDelete, mediaToDelete, projectId, taskId } = observation;

      // Convert File[] to PhotoItem[] for offline sync
      // Offline photos are always new photos (NewPhoto type)
      const photoItems = photos.map(file => ({
        isExisting: false as const,
        file: file,
        gpsCoords: undefined // GPS will be extracted from EXIF or use observation geometry
      }));

      // Add existing photos marked for deletion to photoItems array
      const photoDeletionItems = (photosToDelete || []).map(photoId => ({
        isExisting: true as const,
        photo_id: photoId,
        url: '',
        file_path: '',
        obs_species_id: Number(observationId) || 0,
        project_id: Number(projectId) || 0,
        toDelete: true
      }));

      const allPhotoItems = [...photoItems, ...photoDeletionItems];

      // Offline media are always new media (NewMedia type)
      const mediaItems = media.map((file, index) => {
        const mediaType = (mediaTypes[index] || 'audio') as 'audio' | 'video';
        const thumbnail = mediaThumbnails?.[index] || undefined; // Restore thumbnail from offline storage

        return {
          isExisting: false as const,
          file: file,
          type: mediaType,
          ...(mediaType === 'video' && thumbnail ? { thumbnail } : {}) // Add thumbnail for videos only
        };
      });

      // Add existing media marked for deletion to mediaItems array
      const mediaDeletionItems = (mediaToDelete || []).map(mediaId => ({
        isExisting: true as const,
        media_id: mediaId,
        file_path: '',
        obs_species_id: Number(observationId) || 0,
        type: 'audio' as const,
        toDelete: true
      }));

      const allMediaItems = [...mediaItems, ...mediaDeletionItems];

      let response;
      if (type === 'create') {
        response = await observationApiService.saveObservationWFS(
          observationData,
          observation.geometry || { lng: 0, lat: 0 },
          projectId,
          taskId,
          observation.layerId,
          allPhotoItems, // Include photos to delete
          allMediaItems, // Include media to delete
          mediaTypes,
          observation.auth
        );
      } else if (type === 'update' && observationId) {
        response = await observationApiService.updateObservationWFS(
          observationId,
          observationData,
          observation.geometry,
          projectId,
          taskId,
          observation.layerId,
          allPhotoItems, // Include photos to delete
          allMediaItems, // Include media to delete
          mediaTypes,
          observation.auth
        );
      }

      const isSuccess = response && (response.status_code === 1 || response.status === true);

      // After a successful create sync, call storeLocationAddress — mirrors the online flow
      // (only for create, not update; fire-and-forget so failures do not affect sync success)
      if (isSuccess && type === 'create' && response?.obs_species_id && observation.geometry) {
        try {
          await observationApiService.storeLocationAddress(
            response.obs_species_id,
            observation.geometry.lat,
            observation.geometry.lng
          );
          console.log(`Stored location address for synced observation ${response.obs_species_id}`);
        } catch (addressError) {
          // Non-critical — do not break the sync flow
          console.error('Error storing location address after offline sync:', addressError);
        }
      }

      return isSuccess;
    } catch (error) {
      console.error('Error in syncSingleObservation:', error);
      return false;
    }
  }

  // Schedule a retry after delay
  private scheduleRetry(): void {
    setTimeout(() => {
      console.log('Retrying failed synchronizations...');
      this.syncPendingObservations();
    }, this.retryDelayMs);
  }

  // Check if there are pending observations
  async hasPendingObservations(): Promise<boolean> {
    try {
      const pending = await offlineStorageService.getPendingObservations();
      return pending.length > 0;
    } catch (error) {
      console.error('Error checking pending observations:', error);
      return false;
    }
  }

  // Get count of pending observations
  async getPendingCount(): Promise<number> {
    try {
      const pending = await offlineStorageService.getPendingObservations();
      return pending.length;
    } catch (error) {
      console.error('Error getting pending count:', error);
      return 0;
    }
  }

  // Add a listener for sync completion
  addSyncListener(listener: SyncListener) {
    this.listeners.push(listener);
  }

  // Remove a listener
  removeSyncListener(listener: SyncListener) {
    this.listeners = this.listeners.filter(l => l !== listener);
  }
}

const offlineSyncService = new OfflineSyncService();
export default offlineSyncService;